import Chip from '@components/Chip/Chip';
import {
  ReportStatus,
  ReportSummary,
} from '@components/ReportList/reportList.types';
import { SortedColumn } from '@components/ReportList/types/reportList.types';
import { Table } from '@components/Table/Table';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { XStack } from 'tamagui';

interface ReportTableProps {
  reports: ReportSummary[];
  loading?: boolean;
  selectable?: boolean;
  onReportPress?: (reportId: string) => void;
  selectedReportIds?: string[];
  onSelectionChange?: (selectedReports: ReportSummary[]) => void;

  onSortingChange: (columns: SortedColumn[]) => void;
}
export default function ReportTable({
  reports,
  selectable = true,
  onReportPress,
  selectedReportIds,
  onSelectionChange,
  onSortingChange,
}: ReportTableProps): React.ReactNode {
  const { t } = useAppTranslation();
  const columns: ColumnDef<ReportSummary>[] = [
    {
      accessorKey: 'specie',
      header: t('reports.columns.species'),
      size: 200,
      cell: (info) => info.getValue(),
    },
    {
      id: 'etiologicalData',
      accessorFn: (row) => `${row.etiologyType} / ${row.etiologicalAgent}`,
      header: t('reports.columns.disease'),
      size: 300,
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: 'individualsAffected',
      header: t('reports.columns.individualsAffected'),
      size: 200,
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: 'detectionDate',
      header: t('reports.columns.dateOfDetection'),
      size: 200,
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: 'countryCode',
      header: t('reports.columns.location'),
      size: 130,
      cell: (info): string => {
        const report = info.row.original;
        return report.countryName || report.countryCode || '';
      },
    },
    {
      accessorKey: 'status',
      header: t('reports.columns.status'),
      size: 200,
      cell: (info): React.ReactNode => {
        const status: ReportStatus = info.getValue() as ReportStatus;

        return (
          <XStack>
            <Chip text={status.text} color={status.color} />
          </XStack>
        );
      },
    },
  ];

  const handleSelectionChange = (selectedReports: ReportSummary[]): void => {
    onSelectionChange?.(selectedReports);
  };

  const handleSortingChange = (sorting: SortingState): void => {
    onSortingChange(
      sorting.map(
        (column): SortedColumn => ({
          id: column.id,
          order: column.desc ? 'desc' : 'asc',
        }),
      ),
    );
  };

  return (
    <Table<ReportSummary>
      data={reports}
      idKey="id"
      columnDefs={columns}
      selectable={selectable}
      selectedRowIds={selectedReportIds}
      emptyMessage={t('noData')}
      onSelectionChange={handleSelectionChange}
      onSortingChange={handleSortingChange}
      onRowPress={
        onReportPress ? (row): void => onReportPress(row.id) : undefined
      }
    />
  );
}
